<template>

    <ThemeChanger>

        <Breadcrumb v-if="service.title"
            class="mt-32 lg:mt-48"
            :paths="[
                { route: Route.WhatWeDo, text: 'What we do' },
                { route: { name: Route.Services, params: { slug: service.slug } }, text: service?.title }
            ]"/>

        <PageHeader class="mt-7"
                    v-if="service?.page_header_heading || service?.page_header_content"
                    :header="service?.page_header_heading"
                    :sub-header="service?.page_header_content"
                    :header-span="[ 2, 5 ]"/>

        <!-- <BlockRevealAnimation> -->

            <Grid collapsed class="mt-20 md:mt-28">

                <Picture v-if="service.cover" :image="service.cover" cover width="100%" maxWidth="100%" :height="[ 220, 440 ]"/>

            </Grid>

        <!-- </BlockRevealAnimation> -->

        <AOS type="fade-up" easing="ease" duration="800" delay="1000">

            <Grid class="mt-24">

                <Column>

                    <div class="flex items-center">

                        <img :src="service.service_section_icon?.permalink" alt=""/>

                        <Paragraph class="ml-6 text-black" v-html="service.service_section_title"/>

                    </div>

                </Column>

            </Grid>

        </AOS>

        <AOS type="fade-up" easing="ease" duration="800" delay="1000">

            <Grid>

                <Column v-for="(service, index) of service.service_section_descriptions"
                        :key="index"
                        :span="2"
                        :class="[ index === 0 ? 'sm:mt-10 mt-20' : 'mt-20' ]">

                    <Heading tag="h3" less-margin>
                        {{ service.title }}
                    </Heading>

                    <Paragraph class="text-gray-600 md:pr-10">
                        {{ service.description }}
                    </Paragraph>

                </Column>

            </Grid>

        </AOS>

        <Container fluid v-for="(feature, index) of service.service_section_tech_and_team" :key="index">

            <TitleAndList :title="feature.title" :list="feature.items" :index="index" class="mt-28"/>

        </Container>

        <AOS type="fade-up" easing="ease" :duration="800" :delay="2000">

            <AgileTeams class="mt-24 md:mt-48"/>

        </AOS>

        <AOS type="fade" easing="ease" :duration="800" :delay="500">

            <Grid class="mt-24 md:mt-48">

                <Column :span="[ 2, 5, 4 ]">

                    <Paragraph heading v-html="service.related_cases_title"/>

                    <Heading tag="h2" class="mt-12 md:mt-20" v-html="service.related_cases_heading"/>

                </Column>

            </Grid>

        </AOS>

        <Projects v-if="service.related_cases" :data="service.related_cases" class="pt-16 pb-24 md:pb-48"/>

    </ThemeChanger>

    <ThemeChanger :to="Theme.Blue" v-if="service.include_open_source">

        <Grid class="md:pt-24">

            <Column :span="[ 2, 4 ]">

                <AOS type="fade-up" easing="ease" :duration="800" :delay="500">

                    <Paragraph heading v-html="service.open_source_section_title"/>

                </AOS>

            </Column>

            <Column v-if="service.open_source_section_content" :span="3" class="items-end">

                <AOS type="fade-up" easing="ease" :duration="800" :delay="500">

                    <DynamicContent :content="service.open_source_section_content"/>

                </AOS>

            </Column>

        </Grid>

        <Grid class="mt-16 lg:mt-20">

            <Column :span="[ 2, 5, 4 ]">

                <AOS type="fade-up" easing="ease" :duration="800" :delay="500">

                    <Heading tag="h2" v-html="service.open_source_section_heading"/>

                </AOS>

            </Column>

        </Grid>

        <Container fluid class="mt-16 md:mt-20 space-y-24" :class="{ 'pb-24 md:pb-48': !service.open_source_section_entry }">

            <template v-if="isLoading">

                <!-- <AOS type="fade-up" easing="ease" :duration="800" :delay="500"> -->

                    <OpenSourcePackageSkeleton v-for="rows of 2" :key="rows"/>

                <!-- </AOS> -->

            </template>

            <template  v-else>

                <!-- <AOS type="fade-up" easing="ease" :duration="800" :delay="500 * (index + 1)"> -->

                    <OpenSourcePackage v-for="(pkg, index) of openSourcePackages" :key="index" :data="pkg.data" featured/>

                <!-- </AOS> -->

            </template>

        </Container>

        <Container fluid class="mt-16 md:mt-24 pb-24 lg:pb-48" v-if="service.open_source_section_entry">

            <Grid>

                <Column :span="[ 2, 6 ]">

                    <AOS type="fade-up" easing="ease" :duration="800" :delay="500">

                        <Button style="height: 75px" :route="service.open_source_section_entry.slug">
                            {{ service.open_source_section_label }}
                        </Button>

                    </AOS>

                </Column>

            </Grid>

        </Container>

    </ThemeChanger>

    <ThemeChanger :to="Theme.Dark">

        <Container class="pt-24 lg:pt-48" fluid>

            <AOS type="fade-up" easing="ease" :duration="800" :delay="1000">

                <Grid>

                    <Column :span="[ 2, 3 ]" class="services__reason__heading pb-12">

                        <Heading class="lg:pr-20" tag="h2" v-html="service.why_section_title"/>

                    </Column>

                    <Column :span="[ 2, 3 ]" class="services__reason pb-12">

                        <Paragraph v-html="service.why_section_description"/>

                    </Column>

                </Grid>

            </AOS>

            <Grid v-for="(reason, index) of service.why_section_reasons" :key="index">

                <AOS type="fade-up" easing="ease" :duration="800" :delay="700">

                    <Column :span="3" class="services__reason__heading sm:pt-12 sm:pb-6 md:py-12">

                        <Heading class="lg:pr-20" tag="h2" v-html="reason.title"/>

                    </Column>

                </AOS>

                <AOS type="fade-up" easing="ease" :duration="800" :delay="700">

                    <Column :span="3" class="services__reason sm:pb-12 md:py-12">

                        <Paragraph v-html="reason.description"/>

                    </Column>

                </AOS>

            </Grid>

        </Container>

    </ThemeChanger>

    <MetaInfo v-if="service.seo" :seo="service.seo" :structured-data="service.seo_structured_data"/>

</template>

<script lang="ts">

    import { defineComponent, ref, watchEffect, nextTick, getCurrentInstance } from 'vue'
    import Grid from '../components/Grid/Grid.vue'
    import Column from '../components/Grid/Column.vue'
    import Breadcrumb from '../components/Breadcrumb.vue'
    import AgileTeams from '../components/AgileTeams.vue'
    // import BlockRevealAnimation from '../components/BlockRevealAnimation.vue'
    import Button from '../components/Button.vue'
    import Container from '../components/Container.vue'
    import DynamicContent from '../components/DynamicContent.vue'
    import PageHeader from '../components/PageHeader.vue'
    import OpenSourcePackage from '../components/OpenSourcePackage.vue'
    import OpenSourcePackageSkeleton from '../components/OpenSourcePackageSkeleton.vue'
    import Heading from '../components/Heading.vue'
    import Paragraph from '../components/Paragraph.vue'
    import Projects from '../components/Projects.vue'
    import Picture from '../components/Picture.vue'
    import TitleAndList from '../components/TitleAndList.vue'
    import MetaInfo from '../components/MetaInfo.vue'
    import ThemeChanger from '../components/ThemeChanger.vue'
    import { useCms } from '../compositions/UseCms'
    import { Route } from '../enums/Route'
    import { Theme } from '../enums/Theme'
    import { useTheme } from '../compositions/UseTheme'
    import { useRoute, useRouter } from 'vue-router'

    export default defineComponent({
        name: 'Services',
        components: {
            AgileTeams,
            Breadcrumb,
            Button,
            // BlockRevealAnimation,
            DynamicContent,
            Grid,
            Column,
            Container,
            PageHeader,
            Projects,
            OpenSourcePackage,
            OpenSourcePackageSkeleton,
            Picture,
            Paragraph,
            Heading,
            MetaInfo,
            TitleAndList,
            ThemeChanger
        },
        setup() {

            const refreshAOSHard = getCurrentInstance()?.appContext.config.globalProperties.$aos?.refreshHard
            const { isCNLocale } = useCms()
            const route = useRoute()
            const router = useRouter()
            const data = ref({})
            const openSourceUrls = ref([])
            const openSourcePackages = ref([])
            const isLoading = ref<boolean>(true)
            useTheme().initializeCurrentThemeBasedOnRouteMeta()

            watchEffect(() => {

                if (route.name === Route.Services) {

                    const path = isCNLocale.value ? `/cms/cn/services/${ route.params.slug }.json` : `/cms/en/services/${ route.params.slug }.json`

                    fetch(path)
                        .then(response => response.json())
                        .then(response => {

                            data.value = response

                            if (response.open_source_items !== null && !response.open_source_items.isEmpty) {

                                for (const item of response.open_source_items) {

                                    openSourceUrls.value.push(item.api_url)

                                }

                            }

                        }).then(() => {

                            Promise.all(openSourceUrls.value.map(url => fetch(url))).then(responses =>

                                Promise.all(responses.map(res => res.json()))

                            )
                                .then(responses => {

                                    openSourcePackages.value = responses

                                })

                            isLoading.value = false

                        })
                        .then(nextTick)
                        .then(() => {

                            refreshAOSHard?.()

                        })
                        .catch(err => {

                            console.log(err)
                            router.replace({ name: Route.NotFound })

                        })

                }

            })

            return {
                Theme,
                Route,
                service: data,
                openSourcePackages,
                isLoading
            }

        }
    })

</script>

<style lang="scss">

    @include component(services) {

        @include part(feature-item) {

            @apply py-5;

            margin-top: 0;
            border-bottom: 1px solid #cccccc;

            @screen md {

                &:first-child {

                    padding-top: 0;

                }

            }

        }

        @include part(reason) {

            @include part(heading) {

                border-bottom: none;

                @screen md {

                    border-bottom: 1px solid #000;

                }

            }

            border-bottom: 1px solid #000;

        }

    }

</style>
